import React, { useMemo, useState } from "react";
import { Search } from "lucide-react";

import { useAppSelector } from "../../../app/store";

import {
  getEnrichedStakeholderById,
  getStakeholderById,
  getStakeholders,
} from "../logic/stakeholderSlice";

export interface StakeholderSelectionListProps {
  excludedStakeholderIds?: string[];
  onSelectionChange?: (selectedStakeholderIds: string[]) => void;
}

const StakeholderSelectionList: React.FC<StakeholderSelectionListProps> = ({
  excludedStakeholderIds = [],
  onSelectionChange,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStakeholderIds, setSelectedStakeholderIds] = useState<string[]>([]);

  const stakeholders = useAppSelector(getStakeholders);

  const filteredStakeholders = useMemo(() => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();

    const availableStakeholders = stakeholders.filter(
      (s) => !excludedStakeholderIds.includes(s.stakeholder_id),
    );

    if (!normalizedSearchTerm) {
      return availableStakeholders;
    }

    return availableStakeholders.filter((stakeholder) => {
      const name = stakeholder.name?.toLowerCase() ?? "";
      return name.includes(normalizedSearchTerm);
    });
  }, [stakeholders, excludedStakeholderIds, searchTerm]);

  const handleStakeholderSelect = (stakeholderId: string) => {
    const isSelected = selectedStakeholderIds.includes(stakeholderId);
    const nextSelectedStakeholderIds = isSelected ? [] : [stakeholderId];

    setSelectedStakeholderIds(nextSelectedStakeholderIds);
    onSelectionChange?.(nextSelectedStakeholderIds);
  };

  return (
    <div className="flex w-full flex-col gap-3 font-[Inter]">
      <div
        className="
          flex h-9 w-full items-center gap-2 rounded-[8px] border
          border-[#E5E5E5] bg-white px-3
          shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]
        "
      >
        <Search
          size={16}
          strokeWidth={1}
          className="shrink-0 text-[#737373]"
        />

        <input
          type="text"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder="Search"
          className="
            min-w-0 flex-1 bg-transparent text-[14px] font-normal
            leading-[20px] text-[#0A0A0A] outline-none
            placeholder:text-[#737373]
          "
        />
      </div>

      {filteredStakeholders.length > 0 ? (
        <div
          className="
            flex max-h-[280px] w-full flex-col overflow-y-auto
            rounded-[8px] border border-[#E5E5E5] bg-white
            shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]
          "
        >
          {filteredStakeholders.map((stakeholder) => (
            <StakeholderSelectionItem
              key={stakeholder.stakeholder_id}
              stakeholderId={stakeholder.stakeholder_id}
              isSelected={selectedStakeholderIds.includes(stakeholder.stakeholder_id)}
              onSelect={handleStakeholderSelect}
            />
          ))}
        </div>
      ) : (
        <div
          className="
            flex min-h-[120px] w-full items-center justify-center
            rounded-[8px] border border-[#E5E5E5] bg-white
            shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]
          "
        >
          <span className="text-[14px] font-normal leading-[20px] text-[#737373]">
            No stakeholders available
          </span>
        </div>
      )}
    </div>
  );
};

export default StakeholderSelectionList;

interface StakeholderSelectionItemProps {
  stakeholderId: string;
  isSelected: boolean;
  onSelect: (stakeholderId: string) => void;
}

const StakeholderSelectionItem: React.FC<StakeholderSelectionItemProps> = ({
  stakeholderId,
  isSelected,
  onSelect,
}) => {
  const stakeholder = useAppSelector((state) =>
    getStakeholderById(state, stakeholderId),
  );

  const enrichedStakeholder = useAppSelector((state) =>
    getEnrichedStakeholderById(state, stakeholderId),
  );

  if (!stakeholder) {
    return null;
  }

  const name = stakeholder.name ?? "--";
  const cifNumber = enrichedStakeholder?.customer_details?.cif_number ?? "--";

  return (
    <button
      type="button"
      onClick={() => onSelect(stakeholderId)}
      className={`
        flex w-full items-center gap-4 border-b border-[#E5E5E5]
        p-3 text-left last:border-b-0
        ${isSelected ? "bg-[#EBEEFE]" : "bg-white"}
      `}
    >
      <span
        className="
          flex h-4 w-4 shrink-0 items-center justify-center
          rounded-full border bg-white
        "
      >
        {isSelected && (
          <span className="h-2 w-2 rounded-full bg-[#3551F3]" />
        )}
      </span>

      <span
        className="
          min-w-0 flex-1 truncate text-[14px] font-normal
          leading-[20px] text-[#0A0A0A]
        "
      >
        {name}
      </span>

      <span className="shrink-0 text-[12px] font-normal leading-[16px] text-[#737373]">
        {cifNumber}
      </span>
    </button>
  );
};